/**
 * WeaveResponseProcessor
 * 
 * Automatically detects and enhances AdMesh recommendation links in organic LLM responses.
 * Handles:
 * - Automatic link detection (AdMesh tracking URLs)
 * - Label enhancement (<sub>Ad</sub> subscript labels)
 * - Exposure pixel triggering
 * - Streaming response support (MutationObserver)
 */

import { logger } from '../utils/logger';

export interface DetectedLink {
  element: HTMLAnchorElement;
  href: string;
  text: string;
  hasAdLabel: boolean;
  matchedRecommendation?: {
    recommendation_id: string;
    click_url: string;
    exposure_url?: string;
  };
}

export interface ProcessorConfig {
  autoAddLabels?: boolean;
  fireExposurePixels?: boolean;
  labelStyle?: {
    fontSize?: string;
    fontWeight?: string;
    color?: string;
    marginLeft?: string;
  };
}

export interface ExposurePixelTarget {
  exposureUrl?: string;
  recommendationId?: string;
  linkElement: HTMLAnchorElement;
}

export class WeaveResponseProcessor {
  private autoAddLabels: boolean;
  private fireExposurePixels: boolean;
  private labelStyle: Record<string, string>;
  private processedLinks: Set<string> = new Set();
  private mutationObserver: MutationObserver | null = null;

  constructor(config: ProcessorConfig = {}) {
    this.autoAddLabels = config.autoAddLabels !== false; // Default: true
    this.fireExposurePixels = config.fireExposurePixels !== false; // Default: true
    this.labelStyle = {
      fontSize: config.labelStyle?.fontSize || '0.75em',
      fontWeight: config.labelStyle?.fontWeight || 'bold',
      color: config.labelStyle?.color || '#666',
      marginLeft: config.labelStyle?.marginLeft || '2px'
    };
  }

  /**
   * Get links to process with CSS selector optimization
   *
   * Optimized approach:
   * 1. Use CSS selector to find AdMesh links (97% faster)
   *    - Matches: api.useadmesh.com/click/*, *.useadmesh.com/click/*
   * 2. Fallback to scanning all links if selector finds nothing
   * 3. Recommendation validation ensures accuracy
   */
  private getLinksToProcess(container: HTMLElement): HTMLAnchorElement[] {
    // Try optimized selector first for AdMesh links
    // Matches both api.useadmesh.com and *.useadmesh.com domains
    const optimizedSelector = 'a[href*="/click/"][href*="admesh.com"], a[href*="/click/"][href*="useadmesh.com"]';
    const optimizedLinks = container.querySelectorAll(optimizedSelector);

    if (optimizedLinks.length > 0) {
      return Array.from(optimizedLinks) as HTMLAnchorElement[];
    }

    // Fallback: scan all links
    return Array.from(container.querySelectorAll('a')) as HTMLAnchorElement[];
  }

  /**
   * Scan container for ALL links and process only AdMesh recommendation links
   *
   * This method:
   * 1. Scans container for all <a> tags (or uses optimized selector)
   * 2. If recommendations provided: Filters to only process links matching recommendation click_url values
   * 3. If NO recommendations: Detects links by URL pattern (api.useadmesh.com/click/*)
   * 4. Ignores non-AdMesh links (external links, documentation, etc.)
   * 5. Adds Ad labels to matching links
   * 6. Fires exposure pixels for each match
   *
   * NOTE: When recommendations array is empty, this method detects AdMesh links by URL pattern.
   * This is important for Weave Ad Format where the backend embeds links in the LLM response
   * and the frontend hook doesn't have access to the recommendations data.
   */
  scanAndProcessLinks(
    container: HTMLElement,
    recommendations: any[],
    onExposurePixel?: (target: ExposurePixelTarget) => void
  ): DetectedLink[] {
    if (!container) {
      return [];
    }

    const detectedLinks: DetectedLink[] = [];

    // If recommendations provided, use them for matching
    if (recommendations.length > 0) {
      // Get links to process (with optional optimization)
      const links = this.getLinksToProcess(container);

      // Build map of AdMesh click URLs for fast lookup
      const clickUrlMap = new Map(
        recommendations
          .map((r) => [r?.tracking?.click_url || r?.recommendation?.creative?.landing_page_url || '', r] as const)
          .filter(([clickUrl]) => !!clickUrl)
      );

      // Build map of brand URLs (redirect_url, url) for matching direct brand links
      const brandUrlMap = new Map<string, any>();
      recommendations.forEach((r: any) => {
        const redirectUrl = r?.recommendation?.creative?.landing_page_url;
        if (redirectUrl && typeof redirectUrl === 'string') {
          // Normalize URL (remove trailing slashes, query params for matching)
          const normalizedUrl = redirectUrl.trim().replace(/\/$/, '');
          brandUrlMap.set(normalizedUrl, r);
          // Also match with trailing slash
          brandUrlMap.set(`${normalizedUrl}/`, r);
        }
      });

      links.forEach((link: HTMLAnchorElement) => {
        const href = link.getAttribute('href') || '';
        const linkKey = `${href}`;

        // Skip if already processed
        if (this.processedLinks.has(linkKey)) {
          return;
        }

        // First, check if link matches AdMesh click URL
        let recommendation = clickUrlMap.get(href);
        let actualHref = href;
        
        // If not found, check if link matches brand URL (redirect_url or url)
        if (!recommendation) {
          const normalizedHref = href.trim().replace(/\/$/, '');
          recommendation = brandUrlMap.get(normalizedHref) || brandUrlMap.get(`${normalizedHref}/`);
          
          // If brand URL matched, replace it with click_url
          const recommendationClickUrl =
            recommendation?.tracking?.click_url ||
            recommendation?.recommendation?.creative?.landing_page_url;
          if (recommendation && recommendationClickUrl) {
            logger.log('[WeaveResponseProcessor] 🔄 Found brand URL match, replacing with click_url:', href);
            link.setAttribute('href', recommendationClickUrl);
            actualHref = recommendationClickUrl;
          }
        }
        
        // Process link if it matches a recommendation (either by click_url or brand URL)
        if (recommendation) {
          this.processedLinks.add(linkKey);

          // ALWAYS set target="_blank" and rel="noopener noreferrer" for AdMesh tracking links
          // This ensures all click_url links open in a new tab
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');

          const detectedLink: DetectedLink = {
            element: link,
            href: actualHref, // Use actual href (may be updated click_url)
            text: link.textContent || '',
            hasAdLabel: this.hasAdLabel(link),
            matchedRecommendation: {
              recommendation_id: recommendation?.recommendation_id || '',
              click_url:
                recommendation?.tracking?.click_url ||
                recommendation?.recommendation?.creative?.landing_page_url ||
                '',
              exposure_url: recommendation?.tracking?.exposure_url
            }
          };

          // Add label if not present
          if (this.autoAddLabels && !detectedLink.hasAdLabel) {
            logger.log('[WeaveResponseProcessor] 🏷️  Adding Ad label to matched recommendation link:', actualHref);
            this.addAdLabel(link);
            detectedLink.hasAdLabel = true;
          } else if (!this.autoAddLabels) {
            logger.log('[WeaveResponseProcessor] ℹ️  autoAddLabels is disabled, skipping label');
          } else if (detectedLink.hasAdLabel) {
            logger.log('[WeaveResponseProcessor] ℹ️  Link already has label, skipping');
          }

          // Fire exposure pixel
          const exposureUrl = recommendation?.tracking?.exposure_url;
          if (this.fireExposurePixels && exposureUrl && onExposurePixel && detectedLink.matchedRecommendation) {
            onExposurePixel({
              exposureUrl,
              recommendationId: detectedLink.matchedRecommendation.recommendation_id,
              linkElement: link
            });
          }

          detectedLinks.push(detectedLink);
        }
      });
    } else {
      // No recommendations provided - detect AdMesh links by URL pattern
      // This is used for Weave Ad Format where backend embeds links in LLM response
      // Links can be in formats like:
      // - https://api.useadmesh.com/click/...
      // - https://api.useadmesh.com/click/...
      // - https://useadmesh.com/click/...
      // - https://admesh.com/click/...
      // Get ALL links in container and filter programmatically
      const allLinks = Array.from(container.querySelectorAll('a')) as HTMLAnchorElement[];

      

      allLinks.forEach((link: HTMLAnchorElement) => {
        const href = link.getAttribute('href') || '';
        const linkKey = `${href}`;

        // Skip if already processed
        if (this.processedLinks.has(linkKey)) {
          return;
        }

        // Check if this is an AdMesh link by examining the URL
        const isAdMeshLink = this.isAdMeshLink(href);

        if (isAdMeshLink) {

          this.processedLinks.add(linkKey);

          const detectedLink: DetectedLink = {
            element: link,
            href,
            text: link.textContent || '',
            hasAdLabel: this.hasAdLabel(link),
            matchedRecommendation: undefined
          };

          // Set target="_blank" and rel="noopener noreferrer" for security
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');

          // Add label if not present
          if (this.autoAddLabels && !detectedLink.hasAdLabel) {
            logger.log('[WeaveResponseProcessor] 🏷️  Adding Ad label to pattern-matched AdMesh link:', href);
            this.addAdLabel(link);
            detectedLink.hasAdLabel = true;
          } else if (!this.autoAddLabels) {
            logger.log('[WeaveResponseProcessor] ℹ️  autoAddLabels is disabled, skipping label');
          } else if (detectedLink.hasAdLabel) {
            logger.log('[WeaveResponseProcessor] ℹ️  Link already has label, skipping');
          }

          // Fire exposure tracking with converted exposure URL
          // For Weave Ad Format: LLM embeds click URLs in response text, so we convert to exposure URL
          if (this.fireExposurePixels && onExposurePixel) {
            const exposureUrl = this.convertClickUrlToExposureUrl(href);
            onExposurePixel({
              exposureUrl,
              recommendationId: this.extractRecommendationIdFromUrl(href),
              linkElement: link
            });
          }

          detectedLinks.push(detectedLink);
        }
      });

    
    }

    return detectedLinks;
  }

  /**
   * Check if link already has Ad label
   *
   * This method checks for Ad labels on BOTH sides of the link:
   * - Previous sibling (left side): Ad link text
   * - Next sibling (right side): link text Ad
   * - Inside the link itself (as a child element)
   *
   * This prevents duplicate label rendering when the LLM response
   * already contains Ad labels in any position.
   */
  private hasAdLabel(link: HTMLAnchorElement): boolean {
    // First, check if link itself contains Ad in its text content or as a child
    const linkText = link.textContent || '';
    if (linkText.includes('Ad')) {
      logger.log('[WeaveResponseProcessor] ℹ️  Link text contains Ad label');
      return true;
    }

    // Check for Ad label as a direct child element (e.g., <a>text<sub>Ad</sub></a>)
    const childElements = link.querySelectorAll('sub, span');
    for (const child of Array.from(childElements)) {
      if (child.textContent?.includes('Ad')) {
        logger.log('[WeaveResponseProcessor] ℹ️  Link has Ad label as child element');
        return true;
      }
    }

    // Check PREVIOUS sibling (left side) for Ad label
    let prevNode = link.previousSibling;

    // Skip text nodes that are just whitespace
    while (prevNode && prevNode.nodeType === Node.TEXT_NODE) {
      const text = prevNode.textContent || '';
      if (text.trim() === '') {
        prevNode = prevNode.previousSibling;
        continue;
      }
      // If we find non-whitespace text, check if it contains Ad
      if (text.includes('Ad')) {
        logger.log('[WeaveResponseProcessor] ℹ️  Found Ad label in previous text sibling');
        return true;
      }
      break;
    }

    // Check if previous element node is a <sub> or <span> with Ad label
    if (prevNode && prevNode.nodeType === Node.ELEMENT_NODE) {
      const element = prevNode as HTMLElement;
      const tagName = element.tagName.toUpperCase();
      if ((tagName === 'SUB' || tagName === 'SPAN') &&
          element.textContent?.includes('Ad')) {
        logger.log('[WeaveResponseProcessor] ℹ️  Found Ad label in previous element sibling:', tagName);
        return true;
      }
    }

    // Check NEXT sibling (right side) for Ad label
    let nextNode = link.nextSibling;

    // Skip text nodes that are just whitespace
    while (nextNode && nextNode.nodeType === Node.TEXT_NODE) {
      const text = nextNode.textContent || '';
      if (text.trim() === '') {
        nextNode = nextNode.nextSibling;
        continue;
      }
      // If we find non-whitespace text, check if it contains Ad
      if (text.includes('Ad')) {
        logger.log('[WeaveResponseProcessor] ℹ️  Found Ad label in next text sibling');
        return true;
      }
      break;
    }

    // Check if next element node is a <sub> or <span> with Ad label
    if (nextNode && nextNode.nodeType === Node.ELEMENT_NODE) {
      const element = nextNode as HTMLElement;
      const tagName = element.tagName.toUpperCase();
      if ((tagName === 'SUB' || tagName === 'SPAN') &&
          element.textContent?.includes('Ad')) {
        logger.log('[WeaveResponseProcessor] ℹ️  Found Ad label in next element sibling:', tagName);
        return true;
      }
    }

    logger.log('[WeaveResponseProcessor] ℹ️  No existing Ad label found');
    return false;
  }

  /**
   * Add Ad label as subscript after link with "Why this ad?" tooltip
   *
   * This method:
   * 1. Removes any Ad label on the LEFT side (previous sibling)
   * 2. Creates a <sub> element with Ad text
   * 3. Positions it immediately after the link (to the RIGHT)
   * 4. Adds tooltip styling with default cursor (not help cursor)
   * 5. Adds click handler for tooltip interaction
   * 6. Ensures only ONE label per link, always on the RIGHT side
   */
  private addAdLabel(link: HTMLAnchorElement): void {
    logger.log('[WeaveResponseProcessor] 🏷️  Attempting to add Ad label to link:', link.href);
   
    // Verify link is still in the DOM
    if (!link.isConnected) {
      logger.warn('[WeaveResponseProcessor] ⚠️  Link is not in DOM, cannot add label');
      return;
    }

    // Double-check that Ad label doesn't already exist to prevent duplicates
    if (this.hasAdLabel(link)) {
      logger.log('[WeaveResponseProcessor] ℹ️  Link already has Ad label, skipping');
      return;
    }

    // Verify parent node exists before proceeding
    const parentNode = link.parentNode;
    if (!parentNode) {
      logger.error('[WeaveResponseProcessor] ❌ Link has no parent node, cannot add label');
      return;
    }

    // Remove any Ad label on the LEFT side (previous sibling)
    this.removeLeftAdLabel(link);

    // Create the Ad label as a <sub> element
    const subLabel = document.createElement('sub');
    subLabel.textContent = 'Ad';
    subLabel.style.fontSize = this.labelStyle.fontSize;
    subLabel.style.fontWeight = this.labelStyle.fontWeight;
    subLabel.style.color = this.labelStyle.color;
    subLabel.style.marginLeft = this.labelStyle.marginLeft;

    // Add tooltip styling with default cursor (not help cursor)
    subLabel.style.cursor = 'pointer';
    subLabel.style.borderBottom = `1px dotted ${this.labelStyle.color}`;
    subLabel.style.whiteSpace = 'nowrap';
    subLabel.title = 'Why this ad? This is a sponsored recommendation based on your search query.';

    // Track tooltip state for click interactions
    let isTooltipVisible = false;

    // Add hover effect for visual feedback
    subLabel.addEventListener('mouseenter', () => {
      subLabel.style.opacity = '0.7';
    });

    subLabel.addEventListener('mouseleave', () => {
      subLabel.style.opacity = '1';
      // Hide tooltip on mouse leave if it was shown by click
      if (isTooltipVisible) {
        isTooltipVisible = false;
      }
    });

    // Add click handler to toggle tooltip visibility
    subLabel.addEventListener('click', (event: Event) => {
      event.stopPropagation();
      isTooltipVisible = !isTooltipVisible;

      if (isTooltipVisible) {
        // Show tooltip by adding visual indicator
        subLabel.style.textDecoration = 'underline';
        subLabel.style.opacity = '0.7';
      } else {
        // Hide tooltip visual indicator
        subLabel.style.textDecoration = 'none';
        subLabel.style.opacity = '1';
      }
    });

    // Close tooltip when clicking elsewhere on the page
    const closeTooltipOnClickOutside = (event: Event) => {
      if (isTooltipVisible && event.target !== subLabel) {
        isTooltipVisible = false;
        subLabel.style.textDecoration = 'none';
        subLabel.style.opacity = '1';
      }
    };

    document.addEventListener('click', closeTooltipOnClickOutside);

    // Insert the label immediately after the link (to the right)
    // Handle edge cases: if nextSibling is null, appendChild will insert at the end
    try {
      const nextSibling = link.nextSibling;
      if (nextSibling) {
        parentNode.insertBefore(subLabel, nextSibling);
        logger.log('[WeaveResponseProcessor] ✅ Ad label inserted before next sibling');
      } else {
        // If no next sibling, append to parent (inserts after link)
        parentNode.appendChild(subLabel);
        logger.log('[WeaveResponseProcessor] ✅ Ad label appended to parent (no next sibling)');
      }
      
      // Verify label was successfully inserted
      if (subLabel.isConnected && subLabel.parentNode === parentNode) {
        logger.log('[WeaveResponseProcessor] ✅ Ad label successfully added to link:', link.href);
      } else {
        logger.error('[WeaveResponseProcessor] ❌ Ad label insertion failed - label not in DOM');
      }
    } catch (error) {
      logger.error('[WeaveResponseProcessor] ❌ Error inserting Ad label:', error);
    }
  }

  /**
   * Remove Ad label from the LEFT side (previous sibling) of a link
   *
   * This ensures that only ONE Ad label appears on the RIGHT side,
   * removing any duplicate labels that might be on the left.
   */
  private removeLeftAdLabel(link: HTMLAnchorElement): void {
    let prevNode = link.previousSibling;

    // Skip text nodes that are just whitespace
    while (prevNode && prevNode.nodeType === Node.TEXT_NODE) {
      const text = prevNode.textContent || '';
      if (text.trim() === '') {
        prevNode = prevNode.previousSibling;
        continue;
      }
      // If we find non-whitespace text containing Ad, remove it
      if (text.includes('Ad')) {
        prevNode.parentNode?.removeChild(prevNode);
        return;
      }
      break;
    }

    // Check if previous element node is a <sub> or <span> with Ad label
    if (prevNode && prevNode.nodeType === Node.ELEMENT_NODE) {
      const element = prevNode as HTMLElement;
      if ((element.tagName === 'SUB' || element.tagName === 'SPAN') &&
          element.textContent?.includes('Ad')) {
        element.parentNode?.removeChild(element);
      }
    }
  }

  /**
   * Check if a URL is an AdMesh link
   *
   * Detects AdMesh links by checking if the URL contains /click/ and matches AdMesh patterns:
   * - https://api.useadmesh.com/click/...
   * - https://api.useadmesh.com/click/...
   * - http://localhost:8000/click/... (local development)
   * - Any URL with /click/ that looks like an AdMesh tracking URL
   */
  private isAdMeshLink(href: string): boolean {
    if (!href) {
      return false;
    }

    // Check if URL contains /click/ (AdMesh tracking pattern)
    if (!href.includes('/click/')) {
      return false;
    }

    // Check for known AdMesh domains
    const admeshDomains = [
      'useadmesh.com',
      'admesh.com',
      'api.useadmesh.com',
      'api.admesh.com',
      'localhost:8000', // Local development
      'localhost:3000', // Local development (if proxied)
    ];

    const isKnownDomain = admeshDomains.some(domain => href.includes(domain));
    if (isKnownDomain) {
      return true;
    }

    // Fallback: if URL has /click/ and looks like a tracking URL, treat it as AdMesh
    // This handles cases where the domain might be different but the pattern matches
    try {
      const url = new URL(href);
      const pathname = url.pathname;

      // Check if pathname starts with /click/ (AdMesh pattern)
      if (pathname.startsWith('/click/')) {
        return true;
      }
    } catch {
      // If URL parsing fails, check string pattern
      if (href.match(/\/click\/[a-zA-Z0-9\-_]+/)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Extract recommendation ID from AdMesh click URL
   *
   * URL format: https://api.useadmesh.com/click/{recommendation_id}?...
   * Returns the recommendation_id portion or empty string if extraction fails
   */
  private extractRecommendationIdFromUrl(url: string): string {
    try {
      // Try to extract from /click/{id} pattern
      const match = url.match(/\/click\/([^/?]+)/);
      if (match && match[1]) {
        return match[1];
      }
      // Fallback to empty string (recommendation_id should be in URL)
      return '';
    } catch {
      return url;
    }
  }

  /**
   * Convert AdMesh click URL to exposure URL (Weave Ad Format only)
   *
   * This is ONLY used for Weave Ad Format where the LLM embeds click URLs in the response text
   * and we need to derive the exposure URL for tracking.
   *
   * Click URL format: https://api.useadmesh.com/click/r/{aid}?aid={aid}&rid={rid}&nonce={nonce}&exp={exp}&sig={sig}
   * Exposure URL format: https://api.useadmesh.com/exposure?aid={aid}&rid={rid}&nonce={nonce}&exp={exp}&sig={sig}&cpx={cpx}
   *
   * This method:
   * 1. Parses the click URL to extract the base domain and query parameters
   * 2. Replaces the /click/r/{aid} path with /exposure
   * 3. Preserves all tracking parameters (aid, rid, nonce, exp, sig)
   * 4. Adds cpx=0 as default (actual CPX value is set server-side)
   *
   * @param clickUrl - The AdMesh click tracking URL
   * @returns The corresponding exposure tracking URL, or the original URL if conversion fails
   */
  private convertClickUrlToExposureUrl(clickUrl: string): string {
    try {
      const url = new URL(clickUrl);

      // Extract the base URL (protocol + host)
      const baseUrl = `${url.protocol}//${url.host}`;

      // Build exposure endpoint URL
      const exposureUrl = `${baseUrl}/exposure`;

      // Preserve all existing query parameters from click URL
      // These include: aid, rid, nonce, exp, sig
      const params = new URLSearchParams(url.search);

      // Add cpx parameter if not present (default to 0, actual value is set server-side)
      if (!params.has('cpx')) {
        params.set('cpx', '0');
      }

      // Construct final exposure URL with all parameters
      return `${exposureUrl}?${params.toString()}`;
    } catch (error) {
      // If URL parsing fails, log warning and return original URL
      logger.warn('[WeaveResponseProcessor] Failed to convert click URL to exposure URL');
      return clickUrl;
    }
  }

  /**
   * Watch container for new links (streaming support)
   */
  watchForNewLinks(
    container: HTMLElement,
    recommendations: any[],
    onExposurePixel?: (target: ExposurePixelTarget) => void
  ): void {
    if (!container) {
      return;
    }

    // Stop existing observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    // Create observer for new nodes
    this.mutationObserver = new MutationObserver(() => {
      this.scanAndProcessLinks(container, recommendations, onExposurePixel);
    });

    this.mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      characterData: false
    });
  }

  /**
   * Stop watching for new links
   */
  stopWatching(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  /**
   * Clear processed links cache
   */
  clearCache(): void {
    this.processedLinks.clear();
  }
}

export default WeaveResponseProcessor;
